/**
 * Oslet Predictive Search Component
 * Handles search modal and predictive search functionality
 */

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('#predictive-search-results');
    this.form = this.querySelector('form');

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Input change handler with debounce
    this.input.addEventListener('input', this.debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));

    // Prevent form submission for empty search
    this.form.addEventListener('submit', (event) => {
      if (!this.input.value.trim()) {
        event.preventDefault();
      }
    });

    // Close on click outside
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) {
        this.close();
      }
    });

    // Instant close if empty
    this.input.addEventListener('input', () => {
      if (!this.input.value.trim().length) {
        this.close();
      }
    });
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    const url = `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[limit]=10&section_id=predictive-search-oslet`;

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search-oslet');

        if (section) {
          this.results.innerHTML = section.innerHTML;
          this.open();
        } else {
          this.close();
        }
      })
      .catch((error) => {
        console.error('Predictive search error:', error);
        this.close();
      });
  }

  open() {
    this.results.style.display = 'block';
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.results.innerHTML = '';
    this.results.style.display = 'none';
    this.input.setAttribute('aria-expanded', 'false');
  }

  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);

/**
 * Search Modal Controller
 */
class SearchModal {
  constructor() {
    this.modal = document.querySelector('[data-search-modal]');
    this.toggleButtons = document.querySelectorAll('[data-search-toggle]');
    this.closeButtons = document.querySelectorAll('[data-search-close]');
    this.input = this.modal?.querySelector('[data-search-input]');

    if (!this.modal) return;

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Open modal
    this.toggleButtons.forEach(button => {
      button.addEventListener('click', () => this.open());
    });

    // Close modal
    this.closeButtons.forEach(button => {
      button.addEventListener('click', () => this.close());
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-active')) {
        this.close();
      }
    });
  }

  open() {
    this.modal.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    // Focus input after animation
    setTimeout(() => {
      this.input?.focus();
    }, 100);
  }

  close() {
    this.modal.classList.remove('is-active');
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  }
}

// Initialize search modal when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SearchModal();
  });
} else {
  new SearchModal();
}

